import { Box, Typography, Button, Container } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import themeColors from "../theme";

export default function TermsOfUsePage() {
    const navigate = useNavigate();
    const { teal } = themeColors;

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", backgroundColor: "#f5f5f5", display: "flex", flexDirection: "column" }}>
            {/* Back Button */}
            <Box sx={{ p: 3, borderBottom: "1px solid #e0e0e0", backgroundColor: "white" }}>
                <Container maxWidth="lg">
                    <Button
                        variant="text"
                        onClick={() => navigate(-1)}
                        sx={{
                            color: teal,
                            fontSize: "1rem",
                            textTransform: "none",
                            "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
                        }}
                    >
                        ← Back
                    </Button>
                </Container>
            </Box>

            {/* Main Content */}
            <Container maxWidth="lg" sx={{ flexGrow: 1, py: 6 }}>
                <Box
                    sx={{
                        backgroundColor: "white",
                        borderRadius: 3,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        p: { xs: 4, md: 6 },
                    }}
                >
                    <Typography variant="h3" component="h1" sx={{ fontWeight: "bold", mb: 2 }}>
                        Terms of Use
                    </Typography>

                    <Typography variant="body2" sx={{ color: "#666", mb: 4 }}>
                        Last Updated: July 8, 2026
                    </Typography>

                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        Welcome to OnCallCME. These Terms of Use ("Terms") govern your access to and use of the OnCallCME
                        platform, website, and services (collectively, the "Services"). By accessing or using our Services,
                        you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Services.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        1. Acceptance of Terms
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        By creating an account or using OnCallCME, you confirm that you are a licensed healthcare professional
                        or authorized to use the Services. You must be at least 18 years old and legally capable of entering
                        into binding contracts. You agree to comply with all applicable laws and regulations in your use of
                        the Services.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        2. Account Registration and Security
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
                        To access certain features, you must register for an account by providing accurate and complete information.
                        You are responsible for:
                    </Typography>
                    <Box component="ul" sx={{ pl: 4, mb: 3 }}>
                        <li>Maintaining the confidentiality of your account credentials</li>
                        <li>All activities that occur under your account</li>
                        <li>Notifying us immediately of any unauthorized use or security breach</li>
                        <li>Ensuring your account information remains current and accurate</li>
                    </Box>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        You may not share your account with others or create multiple accounts for fraudulent purposes.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        3. Permitted Use
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
                        OnCallCME grants you a limited, non-exclusive, non-transferable license to access and use the Services
                        for your personal professional purposes. You agree to use the Services only for lawful purposes and in
                        accordance with these Terms.
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
                        <strong>You may NOT:</strong>
                    </Typography>
                    <Box component="ul" sx={{ pl: 4, mb: 3 }}>
                        <li>Use the Services for any illegal or unauthorized purpose</li>
                        <li>Attempt to gain unauthorized access to any part of the Services or other users' accounts</li>
                        <li>Interfere with or disrupt the Services or servers connected to the Services</li>
                        <li>Reproduce, duplicate, copy, sell, or exploit any portion of the Services without permission</li>
                        <li>Use any automated system (bots, scrapers) to access the Services</li>
                        <li>Upload or transmit viruses, malware, or any malicious code</li>
                        <li>Impersonate any person or entity or misrepresent your affiliation</li>
                        <li>Collect or harvest information about other users</li>
                    </Box>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        4. CME Tracking and Fund Management
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        OnCallCME provides tools to help you track CME credits and manage CME funds. While we strive for
                        accuracy, you are solely responsible for:
                    </Typography>
                    <Box component="ul" sx={{ pl: 4, mb: 3 }}>
                        <li>Verifying the accuracy of all CME credit information entered into the platform</li>
                        <li>Ensuring compliance with your state and specialty board requirements</li>
                        <li>Maintaining records and documentation required for license renewal</li>
                        <li>Tracking and monitoring your CME fund balances and expenditures</li>
                    </Box>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        OnCallCME does not guarantee acceptance of CME credits by licensing boards or certifying organizations.
                        We are not responsible for any consequences arising from inaccurate data entry or failure to meet CME requirements.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        5. CME Marketplace
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        Our marketplace connects you with third-party CME providers and their educational content. OnCallCME
                        acts as a facilitator and is not the provider of the CME courses. Each course is subject to the terms
                        and conditions of the respective CME provider. We do not guarantee the quality, accreditation, or
                        suitability of any course. Purchases made through the marketplace are subject to the refund policies
                        of the individual providers.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        6. Affiliate Disclosure
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        OnCallCME may receive compensation, including commissions or referral fees, when users purchase
                        products or services through links or listings on our platform. This compensation may influence
                        how products or services are displayed, ranked, or promoted. However, we strive to provide
                        objective and relevant information to users regardless of any such relationships.
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        Where applicable, sponsored or affiliate listings will be clearly identified to users at the
                        point of purchase. OnCallCME is committed to transparency and will label such items as
                        "Sponsored" or "Affiliate" so that users can make informed decisions.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        7. Payment and Fees
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
                        Certain features and services may require payment of fees. By providing payment information, you:
                    </Typography>
                    <Box component="ul" sx={{ pl: 4, mb: 3 }}>
                        <li>Authorize us to charge the applicable fees to your payment method</li>
                        <li>Represent that you are authorized to use the payment method provided</li>
                        <li>Agree to pay all applicable taxes</li>
                    </Box>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        We reserve the right to change our fees with advance notice. Subscription fees are non-refundable
                        except as required by law or as specified in our refund policy.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        8. Intellectual Property Rights
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        The Services, including all content, features, functionality, software, and design, are owned by
                        OnCallCME and are protected by copyright, trademark, and other intellectual property laws. You may
                        not copy, modify, distribute, sell, or lease any part of our Services without our express written permission.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        9. User Content
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        You retain ownership of any content you upload to the Services (documents, certificates, etc.). By
                        uploading content, you grant OnCallCME a license to store, process, and display that content solely
                        for the purpose of providing the Services to you. You represent that you have all necessary rights
                        to upload the content and that it does not violate any third-party rights or applicable laws.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        10. Disclaimer of Warranties
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3, textTransform: "uppercase" }}>
                        THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS
                        OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
                        PARTICULAR PURPOSE, AND NON-INFRINGEMENT. ONCALLCME DOES NOT WARRANT THAT THE SERVICES WILL BE
                        UNINTERRUPTED, SECURE, OR ERROR-FREE, OR THAT DEFECTS WILL BE CORRECTED.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        11. Limitation of Liability
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3, textTransform: "uppercase" }}>
                        TO THE MAXIMUM EXTENT PERMITTED BY LAW, ONCALLCME SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
                        SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR USE, ARISING
                        OUT OF OR RELATED TO YOUR USE OF THE SERVICES, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF
                        SUCH DAMAGES. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE 12 MONTHS
                        PRECEDING THE CLAIM.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        12. Indemnification
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        You agree to indemnify, defend, and hold harmless OnCallCME, its affiliates, officers, directors,
                        employees, and agents from any claims, liabilities, damages, losses, costs, or expenses (including
                        reasonable attorneys' fees) arising out of or related to: (a) your use of the Services; (b) your
                        violation of these Terms; (c) your violation of any rights of another party; or (d) any content you
                        submit or upload.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        13. Termination
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        You may terminate your account at any time by contacting us. We may suspend or terminate your access
                        to the Services at any time, with or without cause or notice, if we believe you have violated these
                        Terms or for any other reason. Upon termination, your right to use the Services will immediately cease.
                        Provisions that by their nature should survive termination shall survive, including ownership provisions,
                        warranty disclaimers, and limitations of liability.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        14. Modifications to Terms
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        We reserve the right to modify these Terms at any time. We will notify you of material changes by
                        posting the updated Terms on our website and updating the "Last Updated" date. Your continued use
                        of the Services after such changes constitutes acceptance of the modified Terms. If you do not agree
                        to the changes, you must stop using the Services.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        15. Governing Law and Dispute Resolution
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        These Terms shall be governed by and construed in accordance with the laws of the State of Texas,
                        without regard to its conflict of law principles. Any disputes arising out of or relating to these
                        Terms or the Services shall be resolved through binding arbitration in accordance with the rules of
                        the American Arbitration Association, except that either party may seek injunctive relief in court
                        to protect intellectual property rights.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        16. Severability
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        If any provision of these Terms is found to be unenforceable or invalid, that provision shall be
                        limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in
                        full force and effect.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        17. Entire Agreement
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        These Terms, together with our Privacy Policy, constitute the entire agreement between you and
                        OnCallCME regarding the use of the Services and supersede all prior agreements and understandings.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        18. Contact Information
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
                        If you have questions about these Terms, please contact us at:
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        <strong>OnCallCME</strong><br />
                        Email: Contact@oncallcme.com<br />
                        Address: 8401 Mayland Dr, Ste V, Richmond, VA 23294
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
}
